import { pickSpawn, type World } from './world';
import type { Tile, RespawnSource } from '../game-api';

/**
 * Turn the server's respawn answer into a tile the hero can actually stand on.
 *
 * The server decides WHERE you wake up — home, then tent, then "origin" — and
 * `origin` is literally `{ tx: 0, ty: 0 }`, documented server-side as "not a
 * place: it means this player has no camp". The client was taking it literally
 * and putting the hero on tile (0,0), which is the top-left corner of the map and
 * on most seeds is open sea. `unstickPlayer` then could not save him either: it
 * searches six tiles for open ground, and from the corner of an ocean there is
 * none. That is the reported bug — a new player, no home and no tent yet, dies to
 * a night mob while exploring, and is teleported into the water permanently
 * stuck, with no death of his own that he necessarily even noticed.
 *
 * The fix belongs on the client because the client is what owns the map: the
 * world is generated from the seed here, so only here is it known which tiles are
 * water. `pickSpawn` is the same function the first-entry spawn uses, so "no camp"
 * now means the same well-chosen patch of dry ground you started on.
 *
 * A named tile (home/tent/camp) is trusted and returned as-is even if it looks
 * blocked — the player put it there, `unstickPlayer` handles a building that has
 * since grown over it, and silently moving someone away from their own tent would
 * be worse than a short shove. Only an out-of-bounds tile is overridden, since
 * that cannot be anybody's camp.
 */
export function respawnTile(
  c: { world: World | null; blockedGrid: Uint8Array | null },
  at: Tile & { source?: RespawnSource },
): Tile {
  const w = c.world;
  if (!w) return at;
  const outside = at.tx < 0 || at.ty < 0 || at.tx >= w.cols || at.ty >= w.rows;
  if (at.source === 'origin' || outside) return pickSpawn(w, c.blockedGrid);
  return { tx: at.tx, ty: at.ty };
}
